import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function HaltonCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Halton Pothole Claims | 27% of U-Roads RED | Fixtyer"
        description="Halton Borough Council reports 27% of unclassified roads in RED condition — up from 5% in 2021 — while filling 11,754 potholes in five years. See what the DfT AMBER rating means for claims in Runcorn and Widnes."
        canonical="/councils/halton"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Halton', url: '/councils/halton' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Halton earns a GREEN spend scorecard — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN, but Halton is AMBER on overall condition and best practice. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired — not on aggregate spend. Halton\'s own data shows 27% of unclassified roads in RED condition and only 13% of projected 2025/26 maintenance spend classed as preventative.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road in Runcorn or Widnes?',
            answer: 'U-roads make up 425km — 69% of Halton\'s 617.4km carriageway network. At the latest visual survey, 27% were in RED condition, up from 5% in 2021. The council itself describes U-road condition scoring as subjective and difficult to repeat accurately — which is relevant when arguing whether Halton had reasonable knowledge of your road\'s state.',
          },
          {
            question: 'Does the Mersey Gateway exclusion affect my claim?',
            answer: 'Halton\'s transparency report explicitly excludes the 31.5km Mersey Gateway carriageway network because it is not maintainable at public expense. Claims for defects on the Mersey Gateway are unlikely to succeed against Halton. The Silver Jubilee Bridge and its approaches are within Halton\'s maintainable network and feature in the council\'s 2025/26 rejuvenation programme.',
          },
          {
            question: 'Pothole fills peaked at 3,592 in 2023/24 — does that mean the roads are fixed?',
            answer: 'No. Halton filled 11,754 potholes over five years while U-road RED condition rose from 5% to 27%. The council estimates around 3,500 pothole repairs for 2025/26 — roughly the same reactive volume as recent years. Filling individual potholes is reactive maintenance; the underlying network condition data shows residential roads deteriorating.',
          },
          {
            question: 'Why is Halton AMBER on best practice as well as condition?',
            answer: 'Halton follows the Well-Managed Highway Infrastructure Code of Practice and uses asset management software, but the council acknowledges U-road visual surveys are subjective and that DfT considers national comparison of this data unreliable. PAS2161-accredited surveying is not mandated until 2026/27. Expect a structured Section 58 defence on classified roads, but identifiable process gaps on the 425km U-road network.',
          },
          {
            question: 'Does Halton mentioning Section 58 in its own report help or hinder my claim?',
            answer: 'It helps you understand the council\'s mindset, not defeat your claim. Halton\'s June 2025 report explicitly references Section 41 statutory duty and the Section 58 special defence. That confirms the council knows claims are possible — your job is to show the specific defect fell outside what their inspection and repair system reasonably covered, especially on U-roads where condition data is acknowledged as unreliable.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Halton Borough Council"
        councilSlug="halton"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/halton"
        headline="Halton Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Halton</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Halton: One in Four Residential Roads Now RED
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Halton Borough Council&apos;s own survey data shows <strong className="text-rose-400">27% of unclassified
            roads in RED condition in 2024</strong> — up from just 5% in 2021. That is roughly 115km of
            residential streets across Runcorn and Widnes in poor condition, while the council filled{' '}
            <strong className="text-amber-400">11,754 potholes in five years</strong>. The DfT rates Halton{' '}
            <strong className="text-amber-400">AMBER overall</strong> despite GREEN spend.
          </p>

          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">27%</div>
            <div className="text-white font-medium mt-2">U-roads in RED condition (2024)</div>
            <div className="text-brand-300 text-sm mt-1">
              Up from 5% in 2021, 14% in 2022 and 22% in 2023 — a sustained five-year climb on the
              425km of residential roads that make up 69% of Halton&apos;s network.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of survey data from Halton&apos;s own DfT transparency return — A-roads stable,
            B and C roads slipping, U-roads in sustained decline
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (86.4km — 14% of network): broadly stable</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">1.1%</div>
                <div className="text-sm text-rose-800">RED (2024)</div>
                <div className="text-xs text-surface-500">down from 1.9% in 2021</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">14.2%</div>
                <div className="text-sm text-amber-800">Amber</div>
                <div className="text-xs text-surface-500">broadly flat since 2020</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">94.5%</div>
                <div className="text-sm text-emerald-800">Green</div>
                <div className="text-xs text-surface-500">up from 84.5% in 2020</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              Main roads including the A56 and A562 are in comparatively good shape. But A-roads are
              just one-seventh of the borough&apos;s maintainable carriageway network.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (106km — 17% of network): amber rising</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">2%</td>
                    <td className="px-4 py-2 text-center">23.6%</td>
                    <td className="px-4 py-2 text-center">74%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">1.6%</td>
                    <td className="px-4 py-2 text-center">13.4%</td>
                    <td className="px-4 py-2 text-center">73.8%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">1.8%</td>
                    <td className="px-4 py-2 text-center">15%</td>
                    <td className="px-4 py-2 text-center">83%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">2.3%</td>
                    <td className="px-4 py-2 text-center">15%</td>
                    <td className="px-4 py-2 text-center">82.6%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">2%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">16.7%</td>
                    <td className="px-4 py-2 text-center font-bold">81.2%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              Amber B and C roads have climbed from 13.4% to 16.7% since 2021, and green condition has
              fallen from 83% to 81.2%. Nearly one in five classified local roads now needs maintenance
              attention or will soon.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">GREEN Spend, Low Preventative Share</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£7.1m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£7.1m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-rose-400">13%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Halton matches its DfT allocation pound for pound and earns a{' '}
              <span className="text-emerald-400">GREEN spend scorecard</span> — but only 13% of
              projected 2025/26 maintenance is classed as preventative, with 16% reactive. Capital has
              risen sharply since 2020/21 (£1.64m) thanks to Liverpool City Region funding, yet U-road
              RED condition has still quintupled.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road Problem */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 425km Residential Network
          </h2>
          <p className="text-surface-600 mb-8">
            69% of Halton&apos;s maintainable carriageways are unclassified roads — and RED condition
            has climbed every year since 2021
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Approx. km in RED</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">5%</td>
                  <td className="px-6 py-4 text-center text-surface-600">~21km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">14%</td>
                  <td className="px-6 py-4 text-center text-surface-600">~60km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">22%</td>
                  <td className="px-6 py-4 text-center text-surface-600">~94km</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">27%</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">~115km</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="For the unclassified network (U roads) the scoring is based on a visual survey carried out over a number of years to give a full network coverage. This only gives a red/green output and is very subjective to an inspector walking those sections and has proved very difficult to get accurate repeatable data. DfT has shared a view that comparison of this data on a national basis was unreliable."
              source="Halton Borough Council Local Highway Maintenance Transparency Report, Appendix A (June 2025)"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The Subjective Survey Problem</h3>
              <p className="text-surface-600 text-sm mb-3">
                Unlike A, B and C roads — surveyed annually by laser-equipped vehicles — Halton&apos;s
                U-roads rely on visual inspection by highways officers walking sections over several
                years. The council itself admits this is subjective and hard to repeat consistently.
              </p>
              <p className="text-surface-600 text-sm">
                For a pothole claim, that matters: if the council cannot produce reliable, repeatable
                condition data for the road where you were damaged, its Section 58 defence rests on
                inspection records and reactive repair logs — not network-level certainty.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">PAS2161 Transition Pending</h3>
              <p className="text-amber-800 text-sm mb-3">
                Halton notes that from 2026/27 the DfT will mandate BSI PAS2161 surveying with
                accredited suppliers, replacing the current three-category RED/amber/green system
                with five finer-grained categories.
              </p>
              <p className="text-amber-800 text-sm">
                Until then, U-road condition reporting remains the subjective visual method the
                council and DfT both acknowledge is unreliable for national comparison — a gap in
                the evidence base underpinning best practice.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, Halton must show it had a reasonable system for
              knowing the condition of its roads. For the 425km U-road network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last visually surveyed — and was it in the 27% RED category?</li>
              <li>• If subjective U-road data is unreliable nationally, how does Halton defend knowledge of your specific street?</li>
              <li>• With RED U-roads climbing 22 percentage points in four years, what preventative action was taken on residential routes?</li>
              <li>• Does the council&apos;s Horizons asset management system hold independent survey data for your road, or only inspector feedback?</li>
            </ul>
            <p className="text-white font-medium">
              A council that admits its own U-road condition data is subjective and unreliable cannot
              easily claim comprehensive network knowledge — especially when RED rates keep rising.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            11,754 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            Reactive repair volumes surged as U-road condition deteriorated — the scale tells you
            how many defects this network produces
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Change vs prior year</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">540</td>
                  <td className="px-6 py-4 text-center text-surface-500">—</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">1,049</td>
                  <td className="px-6 py-4 text-center text-amber-600">+94%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">3,446</td>
                  <td className="px-6 py-4 text-center text-rose-600">+229%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">3,592</td>
                  <td className="px-6 py-4 text-center">+4%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">3,127</td>
                  <td className="px-6 py-4 text-center text-emerald-600">−13%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">11,754</td>
                  <td className="px-6 py-4 text-center text-surface-500">~6 per day average</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Six Potholes a Day, Every Day</h3>
              <p className="text-surface-600 text-sm">
                Averaged over five years, Halton fills around six potholes per day across its
                617.4km network. The surge from 540 fills in 2020/21 to over 3,400 in each of the
                following two years coincides with U-road RED rates climbing from 5% to 22%. A network
                producing defects at that rate is one where potholes routinely form between inspections.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">3,500 More Planned for 2025/26</h3>
              <p className="text-amber-800 text-sm">
                Halton estimates around 3,500 pothole repairs for 2025/26 — in line with recent
                years. Carriageways are identified via the Highway Infrastructure Asset Management
                Plan, Horizons survey data, inspector feedback and areas of intensive reactive repair.
                Reactive volume at this level is not a sign the underlying network is healthy.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Bridges and network context */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Runcorn, Widnes and the River Crossings
          </h2>
          <p className="text-surface-600 mb-8">
            Halton&apos;s geography shapes which roads you can claim against — and which you cannot
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Maintainable network (617.4km)</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• <strong>A-roads:</strong> 86.4km (14%) — includes A56 major maintenance scheme</li>
                <li>• <strong>B and C roads:</strong> 106km (17%)</li>
                <li>• <strong>U-roads:</strong> 425km (69%) — 27% RED in 2024</li>
                <li>• <strong>Footways:</strong> 748km (574km adjacent to carriageways, 174km independent paths)</li>
                <li>• <strong>Cycleways:</strong> 112km</li>
              </ul>
              <p className="text-surface-500 text-xs mt-4">
                Highway asset valued at £2.3 billion (2021/22) — Halton&apos;s most valuable owned asset.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">What Halton does not maintain</h3>
              <p className="text-amber-800 text-sm mb-3">
                The transparency report explicitly excludes the <strong>31.5km Mersey Gateway
                carriageway network</strong> because it is not maintainable at public expense. Pothole
                damage on the Mersey Gateway is unlikely to succeed as a claim against Halton Borough
                Council.
              </p>
              <p className="text-amber-800 text-sm">
                The <strong>Silver Jubilee Bridge</strong> and its approaches are within Halton&apos;s
                network and receive rejuvenation spray treatment in the 2025/26 programme, alongside
                Ditton Junction, Queensway and the Runcorn Station Quarter roundabout.
              </p>
            </div>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Section 41 of the Highways Act 1980 places a statutory duty on all Highway Authorities to maintain the highway network under their control. A failure to maintain or a failure to repair the highway can result in a breach of Section 41 and a claim for damages / compensation by a highway user. However, Section 58 of the Highways Act 1980 provides a special defence for the Council against a claim for an alleged breach of Section 41."
              source="Halton Borough Council Local Highway Maintenance Report, June 2025"
            />
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">2025/26 Treatment Programme</h3>
            <p className="text-brand-200 mb-4">
              Halton&apos;s capital programme — partly funded by £25m from the Liverpool City Region
              Sustainable Transport Settlement — includes:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• <strong className="text-white">Rejuvenation spray:</strong> Silver Jubilee Bridge, Ditton Junction, Queensway, Runcorn Station roundabout</li>
              <li>• <strong className="text-white">Preservation:</strong> Queensbury Way, Fiddlers Ferry Road, Hale Road, Cronton Lane, Hough Green Lane and others</li>
              <li>• <strong className="text-white">Resurfacing:</strong> Everite Road, Tanhouse Lane, A562 Speke Road, A56/A533 Roundabout, Palacefields Avenue junction</li>
            </ul>
            <p className="text-white font-medium">
              If your pothole was on a road not in this programme — and most U-roads are not — the
              council&apos;s planned works do not automatically excuse an unrepaired defect.
            </p>
          </div>
        </div>
      </section>

      {/* What AMBER means for claims */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against an AMBER Halton Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Halton invests its DfT allocation fully — but residential roads are
            deteriorating faster than preventative works reach them
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council&apos;s Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — matches DfT capital allocation (£7.1m in 2025/26)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-roads in strong condition — 94.5% green, 1.1% red
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Risk-based asset management via Horizons software and HIAMP
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Major bridge and corridor investment — Silver Jubilee, A56, LCR CRSTS funding
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Tarmac term maintenance contract with documented KPIs
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a documented Section 58 defence on A-roads and named programme roads. Generic
                claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — 27% of U-roads RED, up from 5% in 2021
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits U-road surveys are subjective and nationally unreliable
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Only 13% preventative spend projected for 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 11,754 potholes filled in five years — defects form faster than prevention catches them
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER best practice — PAS2161 transition not until 2026/27
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 69% of network is U-roads where condition data is weakest
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and a Tarmac maintenance contract, your claim lives
              or dies on the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (email to Highways@halton.gov.uk, FixMyStreet) — proof of actual notice</li>
              <li>• Photos showing the defect&apos;s size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road&apos;s class — on a U-road, cite the 27% RED rate and subjective survey admission</li>
              <li>• Confirm the location is Halton-maintainable — not Mersey Gateway (excluded from council liability)</li>
              <li>• Whether the road appears in the 2025/26 treatment programme or is outside planned works</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Halton&apos;s own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-16 bg-white border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-4">
            Report the pothole to Halton first
          </h2>
          <p className="text-surface-600 mb-6">
            Halton&apos;s transparency report references Section 41 statutory duty and the Section 58
            special defence. Reporting the defect through the council creates a dated record — useful
            evidence if the pothole was reported before your incident, or if Halton failed to repair
            it within a reasonable time.
          </p>
          <a
            href="https://www3.halton.gov.uk/Pages/traffic/Report-it.aspx"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-8 py-4 bg-brand-900 text-white font-semibold rounded-xl hover:bg-brand-800 transition-colors"
          >
            Report a pothole to Halton Borough Council
            <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <p className="text-surface-500 text-sm mt-4">
            Email Highways@halton.gov.uk with the location and photos. For emergencies call 0303 333 4300.
            Keep any confirmation emails or reference numbers.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Halton?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            Runcorn, Widnes or beyond — a well-documented council demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road 27% RED argument</li>
                <li>• No prior-report search</li>
                <li>• No Mersey Gateway liability check</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 27% U-road RED rate documented</li>
                <li>✅ Subjective survey admission quoted</li>
                <li>✅ 11,754 potholes in five years cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Halton</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=halton"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Halton Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Halton earns a GREEN spend scorecard — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN, but Halton is AMBER on overall condition and
                best practice. Section 58 turns on whether the specific defect that damaged your vehicle
                was reasonably inspected and repaired — not on aggregate spend. Halton&apos;s own data
                shows 27% of unclassified roads in RED condition and only 13% of projected 2025/26
                maintenance spend classed as preventative.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road in Runcorn or Widnes?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 425km — 69% of Halton&apos;s 617.4km carriageway network. At the latest
                visual survey, 27% were in RED condition, up from 5% in 2021. The council itself describes
                U-road condition scoring as subjective and difficult to repeat accurately — which is
                relevant when arguing whether Halton had reasonable knowledge of your road&apos;s state.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the Mersey Gateway exclusion affect my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Halton&apos;s transparency report explicitly excludes the 31.5km Mersey Gateway carriageway
                network because it is not maintainable at public expense. Claims for defects on the Mersey
                Gateway are unlikely to succeed against Halton. The Silver Jubilee Bridge and its approaches
                are within Halton&apos;s maintainable network and feature in the council&apos;s 2025/26
                rejuvenation programme.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Pothole fills peaked at 3,592 in 2023/24 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Halton filled 11,754 potholes over five years while U-road RED condition rose from
                5% to 27%. The council estimates around 3,500 pothole repairs for 2025/26 — roughly the
                same reactive volume as recent years. Filling individual potholes is reactive maintenance;
                the underlying network condition data shows residential roads deteriorating.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why is Halton AMBER on best practice as well as condition?
              </h3>
              <p className="text-surface-600 text-sm">
                Halton follows the Well-Managed Highway Infrastructure Code of Practice and uses asset
                management software, but the council acknowledges U-road visual surveys are subjective and
                that DfT considers national comparison of this data unreliable. PAS2161-accredited surveying
                is not mandated until 2026/27. Expect a structured Section 58 defence on classified roads,
                but identifiable process gaps on the 425km U-road network.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Halton mentioning Section 58 in its own report help or hinder my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It helps you understand the council&apos;s mindset, not defeat your claim. Halton&apos;s
                June 2025 report explicitly references Section 41 statutory duty and the Section 58 special
                defence. That confirms the council knows claims are possible — your job is to show the
                specific defect fell outside what their inspection and repair system reasonably covered,
                especially on U-roads where condition data is acknowledged as unreliable.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www3.halton.gov.uk/Documents/traffic/Annex%20A.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Halton Borough Council Local Highway Maintenance Transparency Report, Appendix A (18 June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
